// src/pages/Services/ECommerceDevelopment.jsx
import React, { useEffect, useState } from 'react';
import './ECommerceDevelopment.css';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import EmailApi from '../../components/common/index';

const ECommerceDevelopment = () => {
  const [showModal, setShowModal] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [errors, setErrors] = useState({});

  const validateForm = (formData) => {
    const newErrors = {};

    const name = formData.get('name')?.trim();
    const email = formData.get('email')?.trim();
    const mobile = formData.get('mobile')?.trim();
    const message = formData.get('message')?.trim();

    if (!name) {
      newErrors.name = 'Full name is required';
    }

    if (!email) {
      newErrors.email = 'Email address is required';
    } else {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        newErrors.email = 'Please enter a valid email (e.g., example@gmail.com)';
      }
    }

    if (!mobile) {
      newErrors.mobile = 'Phone number is required';
    } else {
      const mobileRegex = /^[6-9]\d{9}$/;
      if (!mobileRegex.test(mobile)) {
        newErrors.mobile = 'Please enter a valid 10-digit Indian mobile number';
      }
    }

    if (!message) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Prevent non-numeric input in mobile field
  const handleMobileKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 46, 13].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    // Block if not a number
    if ((e.keyCode < 48 || e.keyCode > 57)) {
      e.preventDefault();
    }
  };

  // Prevent pasting non-numeric content
  const handleMobilePaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    if (!/^\d*$/.test(pasted)) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('Sending...');
    setErrors({});

    const formData = new FormData(e.target);

    if (!validateForm(formData)) {
      setFormStatus('Please correct the errors below.');
      setTimeout(() => setFormStatus(''), 4000);
      return;
    }

    const name = formData.get('name');
    const email = formData.get('email');
    const phone = formData.get('mobile');
    const message = formData.get('message');

    const params = new URLSearchParams();
    params.append('full_name', name);
    params.append('email', email);
    params.append('phone', phone);
    params.append('subject', 'ECommerce Development Inquiry');
    params.append('message', message);

    try {
      const { url, method } = EmailApi.SendMail;
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params,
      });
      if (response.ok) {
        setFormStatus('Message sent successfully 🚀');
        e.target.reset();
        setErrors({});
        setTimeout(() => {
          setFormStatus('');
          setShowModal(false);
        }, 3000);
      } else {
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('API Error:', error);
      setFormStatus('Failed to send message. Please try again.');
      setTimeout(() => setFormStatus(''), 4000);
    }
  };

  useEffect(() => {
    // Smooth scrolling for anchor links
    const handleClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (a) {
        const id = a.getAttribute('href');
        if (id.length > 1) {
          const el = document.querySelector(id);
          if (el) {
            e.preventDefault();
            const top = el.getBoundingClientRect().top + window.scrollY - 78;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        }
      }
    };
    document.addEventListener('click', handleClick);
    // Scroll reveal with IntersectionObserver
    const items = Array.from(document.querySelectorAll('[data-reveal]'));
    const timelineCards = document.querySelectorAll('.ecom-timeline-card');
    const allItems = [...items, ...timelineCards];
    const opts = { threshold: 0.18 };
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const idx = allItems.indexOf(entry.target);
          setTimeout(() => {
            entry.target.classList.add('in-view');
            entry.target.style.opacity = 1;
            entry.target.style.transform = 'translateY(0) scale(1)';
          }, idx * 80);
          obs.unobserve(entry.target);
        }
      });
    }, opts);
    allItems.forEach(i => {
      i.style.opacity = 0;
      i.style.transform = 'translateY(18px) scale(.995)';
      observer.observe(i);
    });
    // Small hero CTA pulse
    const cta = document.querySelector('.primary');
    if (cta) {
      let on = true;
      const interval = setInterval(() => {
        cta.style.boxShadow = on ? "0 18px 60px rgba(0,234,255,0.12)" : "0 8px 30px rgba(255,110,199,0.04)";
        on = !on;
      }, 2700);
      return () => clearInterval(interval);
    }
    // Cleanup
    return () => {
      document.removeEventListener('click', handleClick);
      observer.disconnect();
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && showModal) {
      setShowModal(false);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showModal]);

  return (
    <>
      <section className="ecom-hero" id="hero" aria-label="E-commerce hero">
        <div className="ecom-hero-deco" aria-hidden="true"></div>
        <div className="container ecom-hero-inner">
          <div className="ecom-hero-left">
            <h1>E-Commerce Development</h1>
            <h2>Building powerful, user-friendly<br /> e-commerce solutions tailored for business growth.</h2>
            <p>
              At OAKYA IT SERVICES Pvt. Ltd., we design and develop modern e-commerce platforms — secure, scalable,
              and crafted to convert shoppers into loyal customers.
            </p>
            <div className="ecom-cta-row">
              <button className="btn primary" onClick={() => setShowModal(true)}><i className="fa-solid fa-paper-plane"></i> Get a Quote</button>
              <a className="btn secondary" href="#workflow">See Our Workflow</a>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="ecom-features" aria-label="Essential features">
        <h2>Essential features of E-commerce Development</h2>
        <div className="ecom-features-grid">
          <article className="ecom-feature-card" data-reveal>
            <div className="ecom-feature-icon"><i className="fa-solid fa-cart-shopping"></i></div>
            <div className="ecom-feature-body">
              <h4>User-friendly interface</h4>
              <p>Designs that enable effortless browsing, discovery and checkout across devices.</p>
            </div>
          </article>
          <article className="ecom-feature-card" data-reveal>
            <div className="ecom-feature-icon"><i className="fa-solid fa-lock"></i></div>
            <div className="ecom-feature-body">
              <h4>Secure payment gateway</h4>
              <p>PCI-compliant integrations and multiple gateway options (Razorpay, Stripe, PayPal).</p>
            </div>
          </article>
          <article className="ecom-feature-card" data-reveal>
            <div className="ecom-feature-icon"><i className="fa-solid fa-boxes-stacked"></i></div>
            <div className="ecom-feature-body">
              <h4>Product management system</h4>
              <p>Easy product creation, variations, categories, and inventory management for admin users.</p>
            </div>
          </article>
        </div>
      </section>

      <section id="vision" className="ecom-vision" aria-label="vision and pillars">
        <div className="ecom-vision-left">
          <h2>Our Vision for Next-Level E-commerce Excellence</h2>
          <p>
            We’re committed to crafting intuitive and robust e-commerce platforms, specifically customized
            to empower businesses and accelerate growth.
          </p>
        </div>
        <div className="ecom-pillars" aria-hidden="false">
          <div className="ecom-pillar" data-reveal>
            <div className="ecom-p-icon"><i className="fa-solid fa-paint-brush"></i></div>
            <div>
              <h5>Intuitive Design</h5>
              <p>Effortless shopping journeys that engage and retain customers.</p>
            </div>
          </div>
          <div className="ecom-pillar" data-reveal>
            <div className="ecom-p-icon"><i className="fa-solid fa-shield-halved"></i></div>
            <div>
              <h5>Secure Payments</h5>
              <p>Safe, seamless transaction pathways via reliable gateways.</p>
            </div>
          </div>
          <div className="ecom-pillar" data-reveal>
            <div className="ecom-p-icon"><i className="fa-solid fa-database"></i></div>
            <div>
              <h5>Product Management</h5>
              <p>A streamlined toolkit for catalog creation, editing, and organization.</p>
            </div>
          </div>
          <div className="ecom-pillar" data-reveal>
            <div className="ecom-p-icon"><i className="fa-solid fa-truck"></i></div>
            <div>
              <h5>Order Oversight</h5>
              <p>From order placement to delivery — real-time control at your fingertips.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="workflow" className="ecom-workflow" aria-label="workflow">
        <h2>Our Four-Stage Delivery Method</h2>
        <div className="ecom-timeline">
          <article className="ecom-timeline-card" data-reveal>
            <div className="ecom-t-icon"><i className="fa-solid fa-magnifying-glass-chart"></i></div>
            <h3>Discovery & Strategy</h3>
            <p>We begin with in-depth analysis: business goals, user insights and market trends to lay a strong groundwork.</p>
          </article>
          <article className="ecom-timeline-card" data-reveal>
            <div className="ecom-t-icon"><i className="fa-solid fa-pencil-ruler"></i></div>
            <h3>Design & Build</h3>
            <p>With wireframes and modern UX/UI, we translate strategy into a functional, attractive platform.</p>
          </article>
          <article className="ecom-timeline-card" data-reveal>
            <div className="ecom-t-icon"><i className="fa-solid fa-vial"></i></div>
            <h3>Quality Assurance & Testing</h3>
            <p>Rigorous testing across functionality, performance, security and user flows ensures a polished experience.</p>
          </article>
          <article className="ecom-timeline-card" data-reveal>
            <div className="ecom-t-icon"><i className="fa-solid fa-rocket"></i></div>
            <h3>Deployment & Ongoing Improvement</h3>
            <p>Post-launch monitoring, support and updates keep your store performing at its best, always.</p>
          </article>
        </div>
      </section>

      <section id="partner" className="ecom-partner" aria-label="why partner">
        <div className="ecom-left">
          <h2>Why Partner with Us?</h2>
          <p>
            Deep expertise in delivering enterprise-grade e-commerce tailored to your business aspirations.
            A seamless, phased process blending strategic thinking with flawless execution, and end-to-end support.
          </p>
          <ul className="ecom-benefits">
            <li><span className="ecom-check"><i className="fa-solid fa-circle-check"></i></span> Deep expertise delivering enterprise-grade e-commerce</li>
            <li><span className="ecom-check"><i className="fa-solid fa-circle-check"></i></span> A phased, transparent delivery process</li>
            <li><span className="ecom-check"><i className="fa-solid fa-circle-check"></i></span> Customer-first UX and secure payment integrations</li>
            <li><span className="ecom-check"><i className="fa-solid fa-circle-check"></i></span> Ongoing maintenance and growth support</li>
          </ul>
        </div>
        <div className="ecom-right">
          <div className="ecom-card ecom-highlight">
            <h3>Ready to launch your store?</h3>
            <p>Contact OAKYA IT SERVICES Pvt. Ltd. — based in Vijayawada, Andhra Pradesh. We'll guide you from concept to growth.</p>
            <div className="card-ctas">
              <button className="btn primary" onClick={() => setShowModal(true)}><i className="fa-solid fa-phone"></i> Contact Us</button>
              <a className="btn secondary" href="mailto:info@oakyaitservices.com"><i className="fa-regular fa-envelope"></i> info@oakyaitservices.com</a>
            </div>
          </div>
        </div>
      </section>

      {showModal && (
        <div className="ecom-modal-backdrop show" id="contact-modal" role="dialog" aria-modal="true" aria-hidden="false" onClick={() => setShowModal(false)}>
          <div className="ecom-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ecom-modal-close" aria-label="Close contact form" onClick={() => setShowModal(false)}><i className="fa-solid fa-xmark"></i></button>
            <h3>Contact Us</h3>
            <p>Let’s discuss how we can build your next powerful e-commerce solution.</p>
            <form className="ecom-contact-form" onSubmit={handleSubmit} noValidate>
              <label>
                Full name
                <input 
                  type="text" 
                  name="name" 
                  required 
                  className={errors.name ? 'error' : ''} 
                />
                {errors.name && <span className="ecom-error-text">{errors.name}</span>}
              </label>
              <label>
                Email address
                <input 
                  type="email" 
                  name="email" 
                  required 
                  placeholder="example@gmail.com"
                  className={errors.email ? 'error' : ''} 
                />
                {errors.email && <span className="ecom-error-text">{errors.email}</span>}
              </label>
              <label>
                Phone
                <input 
                  type="tel" 
                  name="mobile"  
                  required 
                  maxLength="10"
                  placeholder="10-digit mobile number"
                  className={errors.mobile ? 'error' : ''}
                  onKeyDown={handleMobileKeyDown}
                  onPaste={handleMobilePaste}
                />
                {errors.mobile && <span className="ecom-error-text">{errors.mobile}</span>}
              </label>
              <label>
                Message
                <textarea 
                  name="message" 
                  rows="4" 
                  required
                  className={errors.message ? 'error' : ''}
                ></textarea>
                {errors.message && <span className="ecom-error-text">{errors.message}</span>}
              </label>
              <div className="ecom-modal-actions">
                <button type="submit" className="btn primary"><i className="fa-solid fa-paper-plane"></i> Send Message</button>
                <button type="button" className="btn ghost" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
              {formStatus && <p className="ecom-form-status">{formStatus}</p>}
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ECommerceDevelopment;